import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useReportArchive } from '../hook/useReportArchive.mjs';
import reportsService from '../service/reportsService.mjs';
import monthlyReportService from '../../auditor/service/monthlyReportService.mjs';
import { money, monthName, shortDate } from '../util/format.mjs';
import { saveBlob } from '../util/download.mjs';

const ROLE_LABEL = { global_director: 'Global Director', finance_director: 'Finance Director' };

const baseName = (r) => `Monthly_Report_${r.region}_${monthName(r.month)}_${r.year}`;

const SELECT = 'min-h-[44px] sm:min-h-0 px-4 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-stanford-red focus:border-transparent';
const ROW_BUTTON = 'px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 whitespace-nowrap';

/** One verdict from the archive check, in words and colour. */
const Verdict = ({ label, value, unknownText }) => {
  const [text, cls] = value === true
    ? ['Verified', 'bg-green-100 text-green-800']
    : value === false
      ? ['Problem found', 'bg-red-100 text-red-800']
      : [unknownText, 'bg-gray-100 text-gray-700'];
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="text-gray-600">{label}</span>
      <span className={`inline-flex px-2.5 py-0.5 rounded-full font-medium ${cls}`}>{text}</span>
    </div>
  );
};

/**
 * Every monthly report that has received final approval, with the files that
 * make up its record.
 *
 * The archive zip is rebuilt by the server on each download, so each row can
 * also be CHECKED: whether the approval chain verifies and whether today's
 * figures still match what was approved. The same result is written into the
 * zip's MANIFEST.txt.
 */
// `scope` (from the Downloads tab): follow the page's fiscal year, quarter and
// region, and drop this list's own pickers - one set of filters per page.
const ArchiveTab = ({ scope = null }) => {
  const navigate = useNavigate();
  const {
    reports, totalCount, regions, years, filters, updateFilter, clearFilters, scoped,
    loading, error, reload,
  } = useReportArchive(scope);

  // reportId -> 'archive' | 'pdf' while that download runs.
  const [busy, setBusy] = useState({});
  // reportId -> { loading } | { result } | { error }
  const [checks, setChecks] = useState({});
  const [notice, setNotice] = useState(null);

  const run = async (report, kind, fn) => {
    setBusy(b => ({ ...b, [report.id]: kind }));
    setNotice(null);
    try {
      await fn();
    } catch (err) {
      setNotice({ type: 'error', text: err.message });
    } finally {
      setBusy(b => { const n = { ...b }; delete n[report.id]; return n; });
    }
  };

  const downloadArchive = (report) => run(report, 'archive', async () => {
    const blob = await monthlyReportService.downloadApprovalArchive(report.id);
    saveBlob(blob, `${baseName(report)}.zip`);
    setNotice({
      type: 'success',
      text: `Downloaded the ${report.region} ${monthName(report.month)} ${report.year} archive. `
        + 'MANIFEST.txt inside says whether it still matches the approval.',
    });
  });

  const downloadPdf = (report) => run(report, 'pdf', async () => {
    const blob = await monthlyReportService.downloadSignedReportPDF(report.id);
    saveBlob(blob, `${baseName(report)}.pdf`);
  });

  const toggleCheck = async (report) => {
    if (checks[report.id] && !checks[report.id].loading) {
      setChecks(c => { const n = { ...c }; delete n[report.id]; return n; });
      return;
    }
    setChecks(c => ({ ...c, [report.id]: { loading: true } }));
    try {
      const result = await reportsService.checkArchivedReport(report.id);
      setChecks(c => ({ ...c, [report.id]: { result } }));
    } catch (err) {
      setChecks(c => ({ ...c, [report.id]: { error: err.message } }));
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-lg py-16 flex flex-col items-center justify-center">
        <div className="w-10 h-10 border-4 border-gray-200 border-t-stanford-red rounded-full animate-spin mb-4"></div>
        <p className="text-gray-600">Loading approved reports...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex flex-wrap items-center gap-3">
        <span>Could not load the archive: {error}</span>
        <button onClick={reload} className="px-3 py-1.5 text-sm border border-red-300 rounded-lg hover:bg-red-100">
          Try again
        </button>
      </div>
    );
  }

  const totalApproved = reports.reduce((t, r) => t + (Number(r.totalAmount) || 0), 0);
  const totalClaims = reports.reduce((t, r) => t + (Number(r.claimsCount) || 0), 0);
  const filtered = filters.region !== 'all' || filters.year !== 'all';

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4 sm:mb-6">
        {[
          ['Approved reports', String(reports.length)],
          ['Total approved', money(totalApproved)],
          ['Claims', String(totalClaims)],
        ].map(([label, value]) => (
          <div key={label} className="bg-white rounded-xl shadow-lg p-5">
            <div className="text-2xl font-bold text-gray-800 leading-tight tabular-nums truncate">{value}</div>
            <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
          </div>
        ))}
      </div>

      {!scoped && (
      <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-3 sm:gap-4">
          {regions.length > 1 && (
            <select
              aria-label="Region"
              value={filters.region}
              onChange={(e) => updateFilter('region', e.target.value)}
              className={SELECT}
            >
              <option value="all">All regions</option>
              {regions.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          )}
          <select
            aria-label="Year"
            value={filters.year}
            onChange={(e) => updateFilter('year', e.target.value)}
            className={SELECT}
          >
            <option value="all">All years</option>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          {filtered && (
            <button
              onClick={clearFilters}
              className="min-h-[44px] sm:min-h-0 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>
      )}

      {notice && (
        <div
          role="status"
          className={`mb-4 px-4 py-3 rounded-lg border ${notice.type === 'error'
            ? 'bg-red-50 border-red-200 text-red-700'
            : 'bg-green-50 border-green-200 text-green-800'}`}
        >
          {notice.text}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full responsive-table">
            <thead className="bg-stanford-red text-white">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold">Period</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Region</th>
                <th className="px-4 py-3 text-right text-sm font-semibold">Total</th>
                <th className="px-4 py-3 text-right text-sm font-semibold">Claims</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Final approval</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Files</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => {
                const period = `${monthName(r.month)} ${r.year}`;
                const check = checks[r.id];
                return (
                  <React.Fragment key={r.id}>
                    <tr className="border-b border-gray-200 hover:bg-gray-50 align-top">
                      <td className="px-4 py-3 text-sm text-gray-800 font-medium" data-cell="title">
                        {period}
                        {r.approvalPath === 'skip_regional' && (
                          <span
                            className="ml-2 inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800"
                            title={r.approvalPathReason || 'The regional director stage was skipped'}
                          >
                            Regional stage skipped
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700" data-label="Region">{r.region}</td>
                      <td className="px-4 py-3 text-sm text-gray-800 font-medium tabular-nums text-right" data-label="Total">
                        {money(r.totalAmount)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 tabular-nums text-right" data-label="Claims">{r.claimsCount}</td>
                      <td className="px-4 py-3 text-sm text-gray-700" data-label="Final approval">
                        <div>{shortDate(r.finalApprovedAt)}</div>
                        {r.finalApprover && (
                          <div className="text-xs text-gray-500">
                            {r.finalApprover}{ROLE_LABEL[r.finalApproverRole] ? `, ${ROLE_LABEL[r.finalApproverRole]}` : ''}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3" data-cell="action">
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => downloadArchive(r)}
                            disabled={!!busy[r.id]}
                            className={ROW_BUTTON}
                            aria-label={`Download the ${r.region} ${period} approval archive`}
                          >
                            {busy[r.id] === 'archive' ? 'Building...' : 'Archive (.zip)'}
                          </button>
                          <button
                            onClick={() => downloadPdf(r)}
                            disabled={!!busy[r.id]}
                            className={ROW_BUTTON}
                            aria-label={`Download the ${r.region} ${period} signed report PDF`}
                          >
                            {busy[r.id] === 'pdf' ? 'Downloading...' : 'Report PDF'}
                          </button>
                          <button
                            onClick={() => toggleCheck(r)}
                            className={ROW_BUTTON}
                            aria-expanded={!!check && !check.loading}
                          >
                            {check?.loading ? 'Checking...' : check ? 'Hide check' : 'Check'}
                          </button>
                          <button
                            onClick={() => navigate(`/monthly-report/${r.id}`, { state: { from: '/reports?tab=downloads' } })}
                            className="px-3 py-1.5 text-sm text-stanford-red border border-stanford-red rounded-lg hover:bg-stanford-red hover:text-white transition-colors whitespace-nowrap"
                          >
                            Open
                          </button>
                        </div>
                      </td>
                    </tr>

                    {check && !check.loading && (
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <td colSpan={6} className="px-4 py-3">
                          {check.error ? (
                            <p className="text-sm text-red-700">Could not check this report: {check.error}</p>
                          ) : (
                            <div className="space-y-2">
                              <div className="flex flex-wrap gap-x-6 gap-y-2">
                                <Verdict label="Approval chain" value={check.result?.chainValid} unknownText="Not checked" />
                                <Verdict label="Figures vs final approval" value={check.result?.figuresMatch} unknownText="Not compared" />
                              </div>
                              <ul className="text-xs text-gray-600 space-y-0.5 font-mono whitespace-pre-wrap">
                                {(check.result?.lines || []).map((line, i) => <li key={i}>{line}</li>)}
                              </ul>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {reports.length === 0 && (
          <div className="p-8 text-center">
            <p className="text-gray-500">
              {totalCount === 0
                ? 'No report has received final approval yet.'
                : 'No approved reports match these filters.'}
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default ArchiveTab;
